import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import lcLogo from "../images/laundry-care-logo.png";
import plLogo from "../images/Push_Laundry_white_logo.png";

const ACTION_CONFIG = {
  cancel: {
    endpoint: "/orders/client/orders/safeguard/cancel",
    eyebrow: "Order cancellation",
    heading: "Cancel order request",
    description: "Check the status of your order cancellation below.",
    confirmationMessage:
      "Please confirm that you want to cancel this order. This action cannot be undone.",
    actionLabel: "Cancel Order",
    loadingText: "We’re securely cancelling your order.",
    successTitle: "Your order has been cancelled",
    successMessage: "Your order has been cancelled successfully.",
  },
  reschedule: {
    endpoint: "/orders/client/orders/safeguard/reschedule",
    eyebrow: "Pickup reschedule",
    heading: "Request a new pickup time",
    description: "Check the status of your pickup reschedule request below.",
    confirmationMessage:
      "Please confirm that you want to request a new pickup time for this order.",
    actionLabel: "Reschedule Order",
    loadingText: "We’re securely submitting your request.",
    successTitle: "Your request has been received",
    successMessage:
      "We'll contact you during Support hours to offer new pickup times.",
  },
};

const TENANT_CONFIG = {
  laundrycare: {
    label: "Laundry Care",
    logo: lcLogo,
    homeUrl: "https://www.laundrycare.biz/",
    logoClass: "w-52 sm:w-60",
    accent: "bg-[#548ff7] hover:bg-[#3f7fe9] focus-visible:outline-[#548ff7]",
    wash: "from-[#eff8ff] via-white to-[#fff1f8]",
  },
  pushlaundry: {
    label: "Push Laundry",
    logo: plLogo,
    homeUrl: "https://www.pushlaundry.com/",
    logoClass: "w-48 sm:w-56",
    accent: "bg-[#294b9b] hover:bg-[#203f87] focus-visible:outline-[#294b9b]",
    wash: "from-[#edf5ff] via-white to-[#e9fbff]",
  },
};

const TENANT_DOMAINS = {
  laundrycare: [
    "lc.dev.giddyops.com",
    "lc.staging.giddyops.com",
    "laundrycare.biz",
  ],
  pushlaundry: [
    "pl.dev.giddyops.com",
    "pl.staging.giddyops.com",
    "pushlaundry.com",
  ],
};

function resolveTenantName() {
  const hostname = window.location.hostname.toLowerCase();
  const tenantFromHostname = Object.entries(TENANT_DOMAINS).find(
    ([, domains]) =>
      domains.some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
      ),
  )?.[0];

  if (tenantFromHostname) return tenantFromHostname;

  const tenantFromEnvironment = import.meta.env.VITE_TENANT_NAME;
  return TENANT_CONFIG[tenantFromEnvironment]
    ? tenantFromEnvironment
    : "laundrycare";
}

function getResponseMessage(body) {
  return (
    body?.data?.message ||
    body?.error?.message ||
    body?.message ||
    (typeof body?.error === "string" ? body.error : "")
  );
}

function getStatusContent(status, config, responseMessage) {
  if (status === "success") {
    return {
      title: config.successTitle,
      message: responseMessage || config.successMessage,
    };
  }
  if (status === "error") {
    return {
      title: responseMessage || "This link is no longer valid.",
      message: "",
    };
  }
  if (status === "ready") {
    return {
      title: "Confirm your request",
      message: config.confirmationMessage,
    };
  }
  return { title: "Please wait a moment", message: config.loadingText };
}

function StatusIcon({ status }) {
  if (status === "loading") {
    return (
      <div
        className="h-8 w-8 animate-spin rounded-full border-[3px] border-blue-100 border-t-[#548ff7]"
        aria-hidden="true"
      />
    );
  }

  if (status === "success") {
    return (
      <svg
        className="h-8 w-8 text-emerald-600"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="m7.5 12 3 3 6-7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === "ready") {
    return (
      <svg
        className="h-8 w-8 text-[#548ff7]"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M8 7V4m8 3V4M5 10h14m-8 4h5m-5 3h3M6 6h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      className="h-8 w-8 text-amber-600"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 8.25v4.5m0 3h.008M10.3 4.72 3.2 17a2 2 0 0 0 1.73 3h14.14a2 2 0 0 0 1.73-3L13.7 4.72a2 2 0 0 0-3.4 0Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SafeguardAction({ action = "cancel" }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState(token ? "ready" : "error");
  const [responseMessage, setResponseMessage] = useState("");

  const config = ACTION_CONFIG[action] || ACTION_CONFIG.cancel;
  const tenant = TENANT_CONFIG[resolveTenantName()];
  const tenantLabel = tenant.label;
  const statusContent = getStatusContent(status, config, responseMessage);

  const processAction = async () => {
    if (!token || status === "loading") return;

    setStatus("loading");
    setResponseMessage("");

    try {
      const prefix = import.meta.env.VITE_API_PREFIX || "";
      const normalizedPrefix = prefix.endsWith("/")
        ? prefix.slice(0, -1)
        : prefix;
      const response = await fetch(`${normalizedPrefix}${config.endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      let body = {};
      try {
        body = await response.json();
      } catch {
        body = {};
      }
      const apiMessage = getResponseMessage(body);

      if (response.ok) {
        setResponseMessage(apiMessage);
        setStatus("success");
        return;
      }

      setResponseMessage(apiMessage || "This link is no longer valid.");
      setStatus("error");
    } catch {
      setResponseMessage("We couldn’t complete your request. Please try again.");
      setStatus("error");
    }
  };

  const stateStyles =
    status === "success"
      ? "border-emerald-200 bg-emerald-50"
      : status === "error"
        ? "border-amber-200 bg-amber-50"
        : "border-blue-100 bg-blue-50";

  return (
    <main
      className={`relative min-h-screen overflow-hidden bg-gradient-to-br ${tenant.wash} px-4 py-6 sm:px-6 sm:py-10`}
    >
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#548ff7]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-[#fd57ad]/10 blur-3xl" />

      <div className="relative mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-2xl flex-col sm:min-h-[calc(100vh-5rem)]">
        <header className="flex justify-center py-4 sm:py-6">
          <a href={tenant.homeUrl} aria-label={`${tenantLabel} home`}>
            <img
              src={tenant.logo}
              className={`h-auto ${tenant.logoClass}`}
              alt={`${tenantLabel} logo`}
            />
          </a>
        </header>

        <section className="my-auto overflow-hidden rounded-3xl border border-white/80 bg-white/95 shadow-[0_24px_70px_-28px_rgba(0,28,92,0.35)] backdrop-blur">
          <div className="px-5 pb-6 pt-7 text-center sm:px-10 sm:pb-9 sm:pt-10">
            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
              {config.eyebrow}
            </span>
            <h1 className="mx-auto mt-4 max-w-lg text-2xl font-bold tracking-tight text-[#001c5c] sm:text-3xl">
              {config.heading}
            </h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-600 sm:text-base">
              {config.description}
            </p>

            <div
              className={`mt-7 flex flex-col items-center rounded-2xl border px-4 py-6 text-center sm:mt-8 sm:px-8 sm:py-8 ${stateStyles}`}
              role={status === "error" ? "alert" : "status"}
              aria-live="polite"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
                <StatusIcon status={status} />
              </div>
              <h2 className="mt-4 text-lg font-bold text-slate-900 sm:text-xl">
                {statusContent.title}
              </h2>
              {statusContent.message && (
                <p className="mt-2 max-w-md text-sm leading-6 text-slate-600 sm:text-base">
                  {statusContent.message}
                </p>
              )}
            </div>

            {status === "ready" && (
              <div className="mt-7">
                <button
                  type="button"
                  onClick={processAction}
                  className={`inline-flex min-h-[48px] w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${tenant.accent}`}
                >
                  {config.actionLabel}
                </button>
              </div>
            )}
          </div>
        </section>

        <footer className="py-5 text-center text-xs text-slate-500 sm:py-6">
          Secure order support from {tenantLabel}
        </footer>
      </div>
    </main>
  );
}

export default SafeguardAction;
